<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex-grow overflow-hidden">
      <aside-content id="catalog" v-model:opened="asideOpened">
        <template #aside>
          <div class="flex flex-col h-full w-full overflow-hidden">
            <!-- MY TAGS -->
            <div class="flex flex-col h-1/2 overflow-hidden border-b border-gray-200">
              <div class="px-4 py-2 font-semibold bg-gray-50">
                <div class="flex justify-between items-center">
                  <div v-if="showSearch" class="w-full">
                    <input v-model="searchInput" type="text" :placeholder="t('general.typeToFilter')" class="w-full px-3 py-1 border rounded text-sm" @blur="hideSearch">
                  </div>
                  <div v-else class="flex items-center space-x-2">
                    <font-awesome-icon class="w-4 h-4 cursor-pointer" icon="fa-light fa-search" @click="showSearchInput" />
                    <div v-if="!showSearch && searchInput.length" class="flex items-center px-2 py-0.5 bg-primary text-white text-xs rounded-full space-x-1">
                      <span>{{ searchInput }}</span>
                      <font-awesome-icon class="w-3 h-3 cursor-pointer" icon="fa-light fa-xmark" @click="clearSearch" />
                    </div>
                    <span>{{ t('favorites.tags') }}</span>
                  </div>

                  <div v-if="!showSearch" class="flex items-center space-x-3">
                    <font-awesome-icon :title="t('general.shareTags')" class="w-4 h-4 cursor-pointer" icon="fa-light fa-share" @click="onShareTags" />
                    <font-awesome-icon :title="t('favorites.createTags')" class="w-4 h-4 cursor-pointer" icon="fa-light fa-plus" @click="doShowAddTag" />
                  </div>
                </div>
                <div>
                  <tag-editor v-model:show="addingTag" :tag="defaultTag" :tags="filteredTags" @ok="onAddTag" />
                </div>
              </div>
              <div class="overflow-y-auto h-full px-2 py-2">
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="tag in filteredTags"
                    :key="tag.tag"
                    class="rounded-md cursor-pointer min-h-[28px] py-1 px-2"
                    :class="[
                      selectedTagId === tag.id ? 'scale-105 ring-2 ring-offset-1 ring-primary-400' : '',
                    ]"
                    :style="editingTag && tag.id === editingTag.id
                      ? { backgroundColor: 'white', border: '1px solid #d1d5db', color: 'black' }
                      : { backgroundColor: tag.color, border: `1px solid ${tag.color}`, color: 'white' }"
                    @click="editingTag && tag.id === editingTag.id ? '' : onTagClick(tag)"
                  >
                    <tag-editor
                      v-if="editingTag && tag.id === editingTag.id"
                      :tag="tag" :tags="filteredTags"
                      :show-delete-action="allowDeleteFavorite"
                      @ok="onEditTag" @delete="onDeleteTag" @cancel="onCancelEditing"
                      @click.stop
                    />
                    <div v-else class="flex items-center p-2.5 h-6">
                      <label>{{ tag.tag }}</label>
                      <font-awesome-icon v-if="((tag.sharedUsers?.length) || (tag.sharedUsersGroups?.length))" class="w-3 h-3 ml-1" icon="fa-light fa-share" />
                      <font-awesome-icon class="w-3 h-3 ml-1 hover:text-primary-500" icon="fa-light fa-pencil" @click="doEditTag(tag)" @click.stop />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SHARED TAGS -->
            <div class="flex flex-col h-1/2 overflow-hidden">
              <div class="px-4 py-2 font-semibold bg-gray-50">
                {{ t('favorites.sharedTags') }}
              </div>
              <div class="overflow-y-auto h-full px-2 py-2">
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="tag in sharedTags"
                    :key="tag.tag"
                    class="relative inline-block"
                    @mouseenter="activePopup = tag.tag"
                    @mouseleave="activePopup = ''"
                  >
                    <div
                      class="flex items-center rounded-md justify-between pl-3 py-1 cursor-pointer text-sm font-medium"
                      :style="{ backgroundColor: tag.color, border: `1px solid ${tag.color}4C`, color: 'white' }"
                      :class="selectedTagId === tag.id ? 'scale-105 ring-2 ring-offset-1 ring-primary-400' : ''"
                      @click="onTagClick(tag, true)"
                    >
                      <span class="truncate">{{ tag.tag }}</span>
                      <span
                        class="ml-2 px-2 py-1 rounded-full text-xs font-bold leading-none"
                        :style="{ backgroundColor: darkenColor(tag.color, 20) }"
                      >
                        {{ getInitials(tag.createdByName) }}
                      </span>
                    </div>

                    <!-- NOTE: Popup, do not increase gap between popup and tag as the hover popup will not stay -->
                    <div
                      v-if="activePopup === tag.tag"
                      class="absolute left-0 top-full z-50 mt-0 w-max min-w-[160px] max-w-[calc(100vw-2rem)] bg-white text-gray-800 text-xs shadow-xl rounded-md p-3 border border-gray-300 pointer-events-auto"
                    >
                      <div class="flex items-center justify-between gap-2 mb-2">
                        <div class="truncate font-semibold">
                          {{ tag.tag }}
                        </div>
                        <div class="text-gray-500 whitespace-nowrap text-sm">
                          {{ tag.articles.length }} {{ t('general.articles') }}
                        </div>
                        <button
                          v-tooltip="t('general.duplicate')"
                          class="text-blue-600 text-xs hover:underline whitespace-nowrap" @click.stop="duplicateSharedTag(tag)"
                        >
                          <font-awesome-icon icon="fa-light fa-copy" class="w-3 h-3 ml-1 mr-1" />
                        </button>
                      </div>

                      <hr class="w-full border-t border-gray-300">

                      <div class="flex items-start gap-3 pt-3">
                        <div
                          class="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          :style="{ backgroundColor: darkenColor(tag.color, 40) }"
                        >
                          {{ getInitials(tag.createdByName) }}
                        </div>

                        <div class="flex flex-col">
                          <div class="text-sm font-medium text-gray-800 truncate">
                            {{ tag.createdByName }}
                          </div>
                          <div class="text-xs text-blue-600 truncate">
                            {{ tag.createdByEmail }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- Toolbar -->
        <div>
          <articles-list-toolbar
            module-name="Favorites" :aside-open="asideOpened" :destination="articlesHeader"
            :select-all="selectAllCheck" :select-all-indeterminate="selectAllIndeterminate"
            :enable-browse-by="true" :buttons="toolbarButtons" :is-any-data-available="listedArticles.length > 0"
            @select-all-change="onSelectAll" @aside-click="asideOpened = !asideOpened" @filter="onFilter" @generate="onGenerate"
            @clear-all-filters="onClearFilter" @clear-filter="onClearFilter"
          />
        </div>
        <articles-list
          ref="articlesListRef" module-name="Favorites" :filter-criteria="filterCriteria"
          :thumbnail-size="getThumbnailSize" :column-attribute="getGroupByCol" :row-attribute="getGroupByRow"
          @selection-changed="onSelectionChanged" @data-changed="onArticlesListDataChanged"
          @article-click="onArticleClick"
        />

        <tx-drawer v-model="filterOpen" width="700px" right>
          <articles-filter
            :criteria="filterCriteria" :selected-filter="selectedFilter" class="pt-8 px-15" @cancel="filterOpen = false"
            @confirm="onFilterConfirm"
          />
        </tx-drawer>

        <tx-drawer
          v-model="articleDetailsOpen" :width="enableNewStyleDetails ? '95%' : '1024px'" right
          @closed="closeArticleDetails"
        >
          <model-details
            v-if="enableNewStyleDetails && clickedArticle" ref="modelDetailsRef" :article="clickedArticle"
            @close="closeArticleDetails" @change-article-selection="onChangeArticle" @change="onArticleClick" @updated="onArticleUpdated" @on-edit-fav="onArticleDetailsEditFav"
          />
          <article-details
            v-else-if="clickedArticle" ref="articleDetailsRef" :article="clickedArticle"
            @change="onArticleClick" @updated="onArticleUpdated" @on-edit-fav="onArticleDetailsEditFav"
          />
        </tx-drawer>

        <!-- Action buttons -->
        <action-bar :active="selectedArticles.length > 0" :buttons="actionBarButtons" />

        <favorites-editor-dialog ref="favoritesDlg" />
        <save-image-dialog ref="saveImageDlg" />
      </aside-content>

      <!-- IMPORT FAVORITES -->
      <tx-drawer v-model="showImportDrawer" right width="700px" @closed="onImportDrawerClosed">
        <tx-data-importer v-if="importFavoritesModel && selectedListAction" ref="refDataImporter" :model="importFavoritesModel" :data="[]" :action="selectedListAction" @import="onImport" @cancel="onImportDrawerClosed" />
      </tx-drawer>

      <!-- share tag -->
      <tx-drawer v-model="showShareTag" width="700px" right>
        <share-tags
          @back="closeSharedTag" @cancel="closeSharedTag" @tag-shared="onTagsShared"
        />
      </tx-drawer>
    </div>
    <status-bar />
  </div>
</template>

<script lang="ts" setup>
import { clone } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import type { IImportModel } from '../admin/type'
import FavoritesModel from './favorites'
import TagEditor from '@/shared/components/favorites/TagEditor.vue'
import SaveImageDialog from '@/modules/browse/components/SaveImageDialog.vue'
import ShareTags from '@/modules/browse/components/ShareTags.vue'
import AsideContent from '@/shared/components/AsideContent.vue'
import StatusBar from '@/shared/components/StatusBar.vue'
import ArticlesList from '@/shared/components/ArticlesList.vue'
import ArticleDetails from '@/modules/articleDetails/ArticleDetails.vue'
import ModelDetails from '@/modules/articleDetails/ModelDetails.vue'
import ArticlesListToolbar from '@/shared/components/articlesListToolbar'
import ActionBar from '@/shared/components/ActionBar.vue'
import ArticlesFilter from '@/shared/components/ArticlesFilter.vue'
import FavoritesEditorDialog from '@/shared/components/favorites/FavoritesEditorDialog.vue'
import TxDrawer from '@/shared/components/TxDrawer.vue'
import type MyArticle from '@/models/myArticle'
import { FilterCriteria } from '@/models/filterCriteria'
import * as sBar from '@/store/status'
import { ArticlesListToolbarActions, appConstants, favoritesConstants, privileges, thumbnailConstants } from '@/models/constants'
import utils from '@/services/utils'
import appConfig from '@/services/appConfig'
import useArticleDetails from '@/shared/composables/articleDetails'
import { useNotificationStore } from '@/store/notification'
import { useUserStore } from '@/store/userData'
import useEventsBus from '@/shared/composables/eventBus'
import { type IDataObject, useBrowseStore } from '@/store/browse'
import FavoriteTag from '@/models/favoriteTag'
import type { UpdateFavorite } from '@/api/t1/model/favoriteModel'
import TxDataImporter from '@/shared/components/txDataImporter/TxDataImporter.vue'
import SimpleFavoriteTag from '@/models/simpleFavoriteTag'
import { useConfirmDialog } from '@/shared/composables/confirmDialog'
import type { ITxDataTableListAction } from '@/shared/components/txDataTable/TxDataTable.types'

enum ActionBarActionType {
  deSelect = 'deSelect',
  copyArticle = 'copyArticle',
  saveImage = 'saveImage',
  editFav = 'editFav',
  assortArticles = 'assortArticles',
}

const { t } = useI18n()
const userStore = useUserStore()
const browseStore = useBrowseStore()
const notificationsStore = useNotificationStore()
const { emitter } = useEventsBus()
const { articleDetailsOpen, modelDetailsRef, articleDetailsRef, clickedArticle, onArticleUpdated, onArticleClick, onChangeArticle, openArticleDetails, closeArticleDetails } = useArticleDetails('Favorites')
const confirmDialog = useConfirmDialog()
const { onAppEvent, offAppEvent } = useEventsBus()

const articlesListRef = ref<typeof ArticlesList>()
const favoritesDlg = ref<InstanceType<typeof FavoritesEditorDialog>>()
const saveImageDlg = ref<InstanceType<typeof SaveImageDialog>>()
const refDataImporter = ref<InstanceType<typeof TxDataImporter>>()
const filterOpen = ref(false)
const articlesHeader = ref({ type: 'tag', value: {} } as IToolbarHeader)
const selectedArticlesArticlesListView = ref([] as MyArticle[])
const selectAllCheck = ref(false)
const selectAllIndeterminate = ref(false)
const asideOpened = ref(false)
const filterCriteria = ref<FilterCriteria[]>([])
const selectedFilter = ref<FilterCriteria>()
const route = useRoute()
const router = useRouter()
let listedArticles = [] as MyArticle[]
let groupByColsData = [] as string[]
let groupByRowsData = [] as string[]
const showShareTag = ref(false)
const activePopup = ref('')
const showSearch = ref(false)
const searchInput = ref('')
const editingTag = ref<ITag>()
const selectedTagId = ref<number>()
const addingTag = ref(false)
const articleIds = [] as number[]
const defaultTag: ITag = { tag: '', color: '', selected: true, indeterminate: false, deleted: false, articles: articleIds, customerId: utils.isDefined(userStore.currentCustomer) ? userStore.currentCustomer.CustomerId : -1 }
const showImportDrawer = ref(false)
const favoritesModel = shallowRef<FavoritesModel>()
const importFavoritesModel = ref<IImportModel>()
const selectedListAction = ref<ITxDataTableListAction>()
const isSharedTagClicked = ref(false)
const myFavorites = ref<FavoriteTag[]>([])
const sharedFavorites = ref<FavoriteTag[]>([])

const selectedArticles = computed({
  get: () => {
    return selectedArticlesArticlesListView.value
  },
  set: (value) => {
    selectedArticlesArticlesListView.value = value
  },
})

const myTags = computed(() => {
  const filtered = myFavorites.value
    .filter(tag => tag.CreatedByUserName.toLowerCase() === userStore.currentUsername.toLowerCase())
    .sort((a, b) => a.Tag.localeCompare(b.Tag))

  return filtered.map(itm => ({
    id: itm.Id,
    tag: itm.Tag,
    color: itm.Color,
    createdByUserName: itm.CreatedByUserName,
    createdByEmail: itm.CreatedByEmail,
    createdByName: itm.CreatedByName,
    sharedUsers: itm.SharedUsers,
    sharedUsersGroups: itm.SharedUsersGroups,
    customerId: itm.CustomerId,
    selected: false,
    indeterminate: false,
    articles: itm.Articles,
    deleted: false,
  }))
})

const filteredTags = computed(() => {
  if (!searchInput.value.trim()) { return myTags.value }
  return myTags.value.filter(tag => tag.tag.toLowerCase().includes(searchInput.value.toLowerCase()))
})

const sharedTags = computed(() => {
  return sharedFavorites.value
    .map(itm => ({
      id: itm.Id,
      tag: itm.Tag,
      color: itm.Color,
      createdByUserName: itm.CreatedByUserName,
      createdByEmail: itm.CreatedByEmail,
      createdByName: itm.CreatedByName,
      sharedUsers: itm.SharedUsers,
      sharedUsersGroups: itm.SharedUsersGroups,
      customerId: itm.CustomerId,
      selected: false,
      indeterminate: false,
      articles: itm.Articles,
      deleted: false,
    }))
})

const totalUniqueArticles = computed(() => {
  const allArticles = new Set<number>()

  // myTags article
  myTags.value.forEach((tag) => {
    tag.articles.forEach(articleId => allArticles.add(articleId))
  })

  // sharedTags articles
  sharedTags.value.forEach((tag) => {
    tag.articles.forEach(articleId => allArticles.add(articleId))
  })

  return allArticles.size
})

const selectedArticlesInfo = computed(() => {
  const selectedArticlesInfo = {
    containsArticles: false,
    containsActiveArticles: false,
    containsInactiveArticles: false,
    containsLinkedArticles: false,
    containsArticlesWithoutRetailWindowUpdateAllowed: false,
    containsPlaceholder: false,
    belongsToSameModel: true,
    containsChildInactiveArticles: false,
    containsGloballyDroppedArticles: false,
    containsRequestArticles: false,
    containsActiveRequests: false,
    containsInactiveRequests: false,
    containsAllLockedArticles: true,
    containsAllUnLockedArticles: true,
  }
  const firstArticleModelNumber = selectedArticles.value.length ? selectedArticles.value[0].ModelNumber : null
  selectedArticles.value.forEach((selectedArticle) => {
    if (selectedArticle._IsRequestArticle) {
      selectedArticlesInfo.containsRequestArticles = true
      if (selectedArticle.Status === 1) {
        selectedArticlesInfo.containsActiveRequests = true
      }
      else {
        selectedArticlesInfo.containsInactiveRequests = true
      }
    }
    else {
      selectedArticlesInfo.containsArticles = true
      if (selectedArticle.Status === 1) {
        selectedArticlesInfo.containsActiveArticles = true
      }
      else {
        selectedArticlesInfo.containsInactiveArticles = true
        if (userStore.activeCatalog!.DataSourceTypeId === appConstants.catalogTypes.inherited) {
          selectedArticlesInfo.containsChildInactiveArticles = true
          selectedArticlesInfo.containsGloballyDroppedArticles = selectedArticle.Status === 3
        }
      }
      if (selectedArticle.Locked === 1) {
        selectedArticlesInfo.containsAllUnLockedArticles = false
      }
      if (selectedArticle.Locked === 0) {
        selectedArticlesInfo.containsAllLockedArticles = false
      }
      if (selectedArticle.CatalogCode !== userStore.activeCatalog!.CatalogCode) {
        selectedArticlesInfo.containsLinkedArticles = true
      }
      if (!selectedArticle.AllowRetailWindowUpdate) {
        selectedArticlesInfo.containsArticlesWithoutRetailWindowUpdateAllowed = true
      }
      if (firstArticleModelNumber !== selectedArticle.ModelNumber) {
        selectedArticlesInfo.belongsToSameModel = false
      }
      if (selectedArticle.ColorId == null || selectedArticle.ColorId === 0) {
        selectedArticlesInfo.containsPlaceholder = true
      }
    }
  })

  return selectedArticlesInfo
})

const allowDeleteFavorite = computed(() => {
  return userStore.userProfile.isValidPrivilege(privileges.favorite.updateStatus)
})

const toolbarButtons = computed(() => [
  {
    id: ArticlesListToolbarActions.importFavorites,
    icon: 'fa-light fa-upload',
    label: t('general.import'),
    disabled: false,
    visible: userStore.userProfile.isValidPrivilege(privileges.favorite.createUpdate),
    onClick: () => showImportFavorites(),
  },
] as IToolbarActionButton<ArticlesListToolbarActions>[])

const actionBarButtons = computed<IActionBarButton[]>(() => {
  const actionBarButtons: IActionBarButton[] = []
  if (selectedArticles.value.length) {
    // DESELECT & COPY ARTICLES
    actionBarButtons.push({ id: ActionBarActionType.deSelect, icon: 'fa-light fa-rectangle-xmark', label: 'catalog.actionBar.deselect', disabled: false, onClick: () => { onSelectAll(false) } })
    actionBarButtons.push({ id: ActionBarActionType.copyArticle, icon: 'fa-light fa-copy', label: 'catalog.actionBar.copy', disabled: false, onClick: onCopyArt })

    // SAVE IMAGES
    if (userStore.activeCatalog?.Config.AllowDownloadImages && selectedArticlesInfo.value.containsRequestArticles === false) {
      actionBarButtons.push({ id: ActionBarActionType.saveImage, icon: 'fa-light fa-download', label: 'catalog.actionBar.saveImages', disabled: false, onClick: onSaveImage })
    }

    if (selectedArticlesInfo.value.containsLinkedArticles === false && selectedArticlesInfo.value.containsRequestArticles === false && !selectedArticlesInfo.value.containsRequestArticles) {
      if (selectedArticlesInfo.value.containsInactiveArticles === false && !isSharedTagClicked.value) {
        if (userStore.userProfile.isValidPrivilege(privileges.favorite.createUpdate)) {
          actionBarButtons.push({ id: ActionBarActionType.editFav, icon: 'fa-light fa-star', label: 'catalog.actionBar.editFav', disabled: false, onClick: onEditFav })
        }
        if (userStore.userProfile.isValidPrivilege(privileges.favorite.createUpdate)) {
          actionBarButtons.push({ id: ActionBarActionType.editFav, icon: 'fa-light fa-star', label: 'catalog.actionBar.removeFav', disabled: false, onClick: onRemoveFav })
        }
      }
    }
  }
  return actionBarButtons
})

const enableNewStyleDetails = computed(() => userStore.activeCatalog?.Config.EnableNewStyleDetails || false)

const getThumbnailSize = computed(() => userStore.thumbnailSize)
const getGroupByCol = computed(() => userStore.groupByCol)
const getGroupByRow = computed(() => userStore.groupByRow)

async function onTagClick(tag: ITag, isSharedTags: boolean = false) {
  if (utils.isDefined(tag)) {
    isSharedTagClicked.value = isSharedTags
    selectedTagId.value = tag.id ? tag.id : 0
    filterCriteria.value = []
    router.push({ ...router.currentRoute.value, query: { t: tag.id } })
    if (!userStore.activeCatalog) { return }
    const newCriteria = new FilterCriteria({
      attribute: '_FavoriteTags',
      exclude: false,
      filterBlank: false,
      isAdvancedFilter: false,
      isSavedView: false,
      mode: 7,
      multipleVals: [tag.id],
      numberVal: 0,
      numberVal2: 0,
      statusVal: null,
      stringVal: '',
    })

    filterCriteria.value = [newCriteria]
    articlesHeader.value = {
      type: 'tag',
      value: {
        id: tag.id,
        name: tag.tag,
        color: 'white',
        bgColor: tag.color,
      },
      email: tag.createdByUserName,
    }

    utils.setTitle(userStore.activeCatalog.CatalogName, tag.tag)
  }
}

async function onFilterConfirm(e: FilterCriteria[]) {
  console.debug('Filtering', JSON.stringify(e))
  filterOpen.value = false

  if (!userStore.activeCatalog) { return }

  filterCriteria.value = e.map(itm => new FilterCriteria(itm))
  articlesHeader.value.type = 'filter'
  articlesHeader.value.name = undefined
  articlesHeader.value.value = filterCriteria.value
  utils.setTitle(userStore.activeCatalog.CatalogName, filterCriteria.value.map(itm => itm.toDisplayString(userStore.myAttributes!)).join(', '))
}

function onClearFilter(e?: FilterCriteria) {
  if (!e) {
    filterCriteria.value = []
    articlesHeader.value = { type: 'tag', value: {}, email: '' }
    utils.setTitle(userStore.activeCatalog!.CatalogName)
  }
  const i = filterCriteria.value.findIndex(itm => itm.attribute === e?.attribute)
  if (i >= 0) {
    filterCriteria.value.splice(i, 1)
    if (filterCriteria.value.length === 0) {
      articlesHeader.value = { type: 'tag', value: {}, email: '' }
      utils.setTitle(userStore.activeCatalog!.CatalogName)
    }
    else {
      utils.setTitle(userStore.activeCatalog!.CatalogName, filterCriteria.value.map(itm => itm.toDisplayString(userStore.myAttributes!)).join(', '))
    }
  }
}

async function onSaveImage() {
  saveImageDlg.value?.showDialog(selectedArticles.value)
}

async function onEditFav() {
  if (!userStore.activeCatalog) { return }

  favoritesDlg.value?.showDialog(selectedArticles.value)
}

// remove all assigned favorites from selected article
async function onRemoveFav() {
  const title = t('favorites.removeFavorites.title')
  const message = t('favorites.removeFavorites.body')

  await confirmDialog(title, message, [], async () => {
    if (userStore.activeCatalog) {
      const articles = selectedArticles.value
      const updatedFavorites: Record<number, Set<string>> = {}
      const catalogCode = userStore.activeCatalog.CatalogCode
      const userId = userStore.userProfile.Id
      const username = userStore.currentUsername

      const existingTagsById: Record<number, any> = {}
      myTags.value.forEach((tag) => {
        existingTagsById[tag.id] = tag
      })

      const tagsToCreateOrUpdate: FavoriteTag[] = []

      articles.forEach((article) => {
        article._FavoriteTags.forEach((fav) => {
          if (!updatedFavorites[fav.Id]) {
            updatedFavorites[fav.Id] = new Set()
          }
        })
      })

      Object.keys(updatedFavorites).forEach((favIdStr) => {
        const favId = Number(favIdStr)
        const tag = existingTagsById[favId]
        if (tag) {
          articles.forEach((article) => {
            const index = tag.articles.indexOf(article.Id)
            if (index !== -1) {
              tag.articles.splice(index, 1)
            }
          })

          tag.LocalUpdatedDate = new Date()
          const favoriteTag = new FavoriteTag(catalogCode, userId, {
            Id: tag.id,
            Tag: tag.tag,
            Color: tag.color,
            CreatedByUserName: username,
            Articles: tag.articles,
          })
          tagsToCreateOrUpdate.push(favoriteTag)
        }
      })

      if (tagsToCreateOrUpdate.length > 0) {
        await Promise.all(
          Object.values(tagsToCreateOrUpdate).map(tag =>
            appConfig.DB!.createUpdateFavorite(catalogCode, userId, tag, userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1),
          ),
        )
      }
      // reapply filter
      if (selectedTagId.value) {
        onTagClick(existingTagsById[selectedTagId.value])
      }
    }
  })
}

function onArticleDetailsEditFav(article: MyArticle) {
  favoritesDlg.value?.showDialog([article])
}

async function onCopyArt() {
  const artNbr = selectedArticles.value.map(itm => itm.ArticleNumber)
  try {
    await navigator.clipboard.writeText(artNbr.join('\n'))
    notificationsStore.addNotification({ message: t('notifications.artsCopied'), type: 'Success' })
  }
  catch (error) {
    notificationsStore.addNotification({ message: t('notifications.copyFailed'), type: 'Alert', details: utils.getErrorMessage(error), actions: ['ShowDetails', 'Support'] })
  }
}

function onSelectionChanged(arts: MyArticle[]) {
  selectedArticles.value = arts
  if (arts.length === 0) {
    selectAllCheck.value = false
    selectAllIndeterminate.value = false
  }
  else if (arts.length === listedArticles.length) {
    selectAllCheck.value = true
    selectAllIndeterminate.value = false
  }
  else {
    selectAllCheck.value = false
    selectAllIndeterminate.value = true
  }
  sBar.setItemValue('selectedArticlesCount', arts.length.toLocaleString())
}

function onSelectAll(val: boolean) {
  if (val) {
    articlesListRef.value?.selectAll()
  }
  else {
    articlesListRef.value?.deselectAll()
  }
}

async function onArticlesListDataChanged(articles: MyArticle[], cols: string[], rows: string[]) {
  listedArticles = articles
  groupByColsData = cols
  groupByRowsData = rows
  sBar.setItemValue('listedArts', articles.length.toLocaleString())
}

function onFilter(filter?: FilterCriteria) {
  selectedFilter.value = filter
  filterOpen.value = true
}

function onGenerate() {
  if (filterCriteria.value.length && utils.isDefined(filterCriteria.value.find(criteria => criteria.attribute === '_Seasons'))) {
    notificationsStore.addNotification({ message: t('notifications.dataHasLinkedArticles'), type: 'Alert' })
  }
  else {
    generateSlides()
  }
}
function generateSlides() {
  let dataObjectValue: IDataObject | null = null
  const viewName = null
  dataObjectValue = {
    isBrowseByAttribute: false,
    articles: listedArticles,
    rowDivider: userStore.groupByRow ? userStore.groupByRow : null,
    columnDivider: userStore.groupByCol ? userStore.groupByCol : null,
    thumbnailSize: (viewName != null ? 'large' : thumbnailConstants.thumbnailSizeNumberToStrEnum[userStore.thumbnailSize]),
    numberOfColumns: groupByColsData.length,
    rows: groupByRowsData,
    viewName,
    browseByAttribute: '',
    columns: groupByColsData,
    bucketData: null,
    title: '',
  }
  browseStore.setDataObjectForMerch(dataObjectValue)
  router.push({ name: 'Merch' })
}

async function gotoRouteSelection() {
  if (!['Favorites'].includes(route.name!.toString())) { return }
  if (userStore.activeCatalog && route.params && route.params.articleId) {
    openArticleDetails()
  }
  else if (articleDetailsOpen.value) {
    clickedArticle.value = null
    closeArticleDetails()
  }
}

async function fetchFavoriteTags() {
  if (userStore.activeCatalog) {
    try {
      myFavorites.value = await appConfig.DB!.myFavorites
        .where({
          CatalogCode: userStore.activeCatalog.CatalogCode,
          Status: 1,
          UserId: userStore.userProfile.Id,
          CustomerId: userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1,
        })
        .toArray()

      const allSharedFavorites = await appConfig.DB!.sharedFavorites
        .where({ CatalogCode: userStore.activeCatalog.CatalogCode, UserId: userStore.userProfile.Id })
        .toArray()

      sharedFavorites.value = allSharedFavorites
    }
    catch (error) {
      console.error('Error fetching favorite tags:', error)
      notificationsStore.addNotification({
        message: t('notifications.failedToLoadTags'),
        type: 'Alert',
      })
    }
  }
}

function getInitials(name) {
  if (!name) { return '' }
  const parts = name.trim().split(' ')
  const initials = parts.map(part => part.charAt(0).toUpperCase()).slice(0, 2).join('')
  return initials
}

function darkenColor(hex, amount = 20) {
  if (!hex.startsWith('#')) { return hex }
  const num = Number.parseInt(hex.slice(1), 16)
  const r = Math.max(0, (num >> 16) - amount)
  const g = Math.max(0, ((num >> 8) & 0x00FF) - amount)
  const b = Math.max(0, (num & 0x0000FF) - amount)
  return `rgb(${r}, ${g}, ${b})`
}

function closeSharedTag() {
  showShareTag.value = false
}

function onShareTags() {
  showShareTag.value = true
}

async function onTagsShared() {
  await fetchFavoriteTags()
}

function showSearchInput() {
  showSearch.value = true
}

function hideSearch() {
  showSearch.value = false
}

function clearSearch() {
  searchInput.value = ''
}

function doEditTag(tag: ITag) {
  if (tag.deleted) { return }

  editingTag.value = tag
}

async function onEditTag(tag: ITag) {
  if (editingTag.value && userStore.activeCatalog) {
    const oldTagName: string = clone(editingTag.value.tag)
    editingTag.value.id = tag.id
    editingTag.value.tag = tag.tag
    editingTag.value.color = tag.color
    const data: UpdateFavorite = {
      Articles: tag.articles,
      Color: tag.color,
      Tag: tag.tag,
      CustomerId: tag.customerId,
    }
    await appConfig.DB!.updateFavoriteTag(userStore.activeCatalog!.CatalogCode, oldTagName, userStore.userProfile.Id, tag.id!, data)
  }
  onCancelEditing()
}

async function onDeleteTag(tag: ITag) {
  const title = t('general.warning')
  const message = t('favorites.deleteFavorites.body', { tagName: tag.tag })

  await confirmDialog(title, message, [], async () => {
    if (userStore.activeCatalog) {
      // get the actual favorites tag from db
      const tagToDelete = await appConfig.DB!.myFavorites.get({ CatalogCode: userStore.activeCatalog!.CatalogCode, UserId: userStore.userProfile.Id, Tag: tag.tag, CustomerId: tag.customerId })
      if (tagToDelete) {
        const favTag = new FavoriteTag(userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, tagToDelete)
        favTag.Status = 0
        favTag.LocalUpdatedDate = new Date()
        await appConfig.DB!.deleteFavorite(userStore.activeCatalog.CatalogCode, [favTag])
        const favoriteTagsAtt = userStore.myAttributes!._FavoriteTags
        if (favoriteTagsAtt) {
          await updateFavoritesTagFilter(favoriteTagsAtt)
        }
      }
    }
  })
  onCancelEditing()
}

function onCancelEditing() {
  editingTag.value = undefined
}

// duplicate shared tags
async function duplicateSharedTag(tag: ITag) {
  // existing tag names
  const tagNamesLower: string[] = myTags.value.map(cur => cur.tag.toLowerCase().trim())

  let tagName = tag.tag
  let index = 1
  if (tagNamesLower.includes(tagName.toLowerCase().trim())) {
    while (tagNamesLower.includes(`${tagName.toLowerCase().trim()}(${index})`)) {
      index++
    }
    tagName = `${tagName}(${index})`
  }

  const validArticlesList: any[] = []
  const articles = await appConfig.DB!.getArticlesByCatalogCodeArticleIds(userStore.activeCatalog!, userStore.linkedCatalogDetails, tag.articles, userStore.currentCustomer)
  articles.forEach((article) => {
    if (article && article.Status === 1 && !article._IsNonSegmented && !article._IsRequestArticle) {
      validArticlesList.push(article.Id)
    }
  })

  const newTag = new FavoriteTag(
    userStore.activeCatalog!.CatalogCode,
    userStore.userProfile.Id,
    {
      Tag: tagName,
      Color: tag.color,
      CreatedByUserName: userStore.currentUsername,
      Articles: validArticlesList,
      LocalUpdatedDate: new Date(),
    },
  )

  await appConfig.DB!.createUpdateFavorite(
    userStore.activeCatalog!.CatalogCode,
    userStore.userProfile.Id,
    newTag,
    userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1,
  )

  const favoriteTagsAtt = userStore.myAttributes!._FavoriteTags
  if (favoriteTagsAtt) {
    await updateFavoritesTagFilter(favoriteTagsAtt)
  }
  onCancelEditing()
}

function doShowAddTag() {
  addingTag.value = true
  onCancelEditing()
}

async function onAddTag(tag: ITag) {
  const createTag = new FavoriteTag(userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, {
    Id: tag.id,
    Tag: tag.tag,
    Color: tag.color,
    CreatedByUserName: userStore.currentUsername,
    Articles: tag.articles,
    LocalUpdatedDate: new Date(),
  })

  await appConfig.DB!.createUpdateFavorite(userStore.activeCatalog!.CatalogCode, userStore.userProfile.Id, createTag, userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1)
  const favoriteTagsAtt = userStore.myAttributes!._FavoriteTags
  if (favoriteTagsAtt) {
    await updateFavoritesTagFilter(favoriteTagsAtt)
  }
  onCancelEditing()
}

function showImportFavorites() {
  importFavoritesModel.value = undefined
  if (favoritesModel.value) {
    selectedListAction.value = {
      id: 'importFavorites',
      icon: '',
      label: '',
      enabled: true,
      visible: true,
    }
    importFavoritesModel.value = favoritesModel.value.getImportData()
    showImportDrawer.value = true
  }
}

function onImportDrawerClosed() {
  showImportDrawer.value = false
}

async function updateFavoritesTagFilter(favoriteTagsAtt: IMyAttribute) {
  const catalogCode = userStore.activeCatalog!.CatalogCode
  const userId = userStore.userProfile.Id
  const userName = userStore.userProfile.UserName

  const favTags = await appConfig.DB!.myFavorites
    .where({ CatalogCode: catalogCode, UserId: userId, Status: 1, CustomerId: userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1 })
    .toArray()

  const sharedTags = await appConfig.DB!.sharedFavorites
    .where({ CatalogCode: catalogCode, UserId: userStore.userProfile.Id })
    .toArray()

  favTags.sort((a, b) => {
    if (a.CreatedByUserName === userName) { return -1 }
    return a.Tag.localeCompare(b.Tag)
  })

  sharedTags.sort((a, b) => a.Tag.localeCompare(b.Tag))

  const allTags = [...favTags, ...sharedTags]

  favoriteTagsAtt.FilterLookup = new Map()

  allTags.forEach((tag) => {
    if (!favoriteTagsAtt.FilterLookup.has(tag.Id)) {
      favoriteTagsAtt.FilterLookup.set(
        tag.Id,
        new SimpleFavoriteTag(tag.Id, tag.Tag, tag.Color, tag.CreatedByUserName, tag.CustomerId),
      )
    }
  })

  emitter('catalogDataUpdated', { source: 'Favorite' })
}

async function onImport(data: any[]) {
  if (!userStore.activeCatalog) { return }

  try {
    const catalogCode = userStore.activeCatalog.CatalogCode
    const userId = userStore.userProfile.Id
    const username = userStore.currentUsername

    const existingTagsByName: Record<string, any> = {}
    myTags.value.forEach((tag) => {
      existingTagsByName[tag.tag.toLowerCase().trim()] = tag
    })

    const tagToArticleNumbersMap: Record<string, Set<string>> = {}

    for (const row of data) {
      const articleNumber = row.ArticleNumber?.toString().trim()
      if (!articleNumber) { continue }

      const rowTags = row.Tags?.toString().split('|').map(tag => tag.trim()).filter(Boolean) || []

      for (const tag of rowTags) {
        const key = tag
        if (!tagToArticleNumbersMap[key]) { tagToArticleNumbersMap[key] = new Set() }
        tagToArticleNumbersMap[key].add(articleNumber)
      }
    }

    const tagsToCreateOrUpdate: FavoriteTag[] = []

    for (const [tagName, articleNumbersSet] of Object.entries(tagToArticleNumbersMap)) {
      const articleNumbers = Array.from(articleNumbersSet)

      const articles = await appConfig.DB!.getArticlesByCatalogCodeArticleNumbers(userStore.activeCatalog!, userStore.linkedCatalogDetails, articleNumbers, userStore.currentCustomer, [], false, userStore.currentCustomerSegmentations, userStore.myAttributes!)

      const articleIds = articles.map(article => article.Id)
      const existingTag = existingTagsByName[tagName.toLowerCase()]

      if (existingTag) {
        const newArticleIds = articleIds.filter(id => !existingTag.articles.includes(id))
        if (newArticleIds.length) {
          existingTag.Articles = [...existingTag.articles, ...newArticleIds]
          existingTag.LocalUpdatedDate = new Date()
          tagsToCreateOrUpdate[tagName] = new FavoriteTag(catalogCode, userId, {
            Id: existingTag.id,
            Tag: existingTag.tag,
            Color: existingTag.color,
            CreatedByUserName: username,
            Articles: existingTag.Articles,
          })
        }
      }
      else {
      // Create new tag
        const color = favoritesConstants.predefinedColors[
          Object.keys(tagsToCreateOrUpdate).length % favoritesConstants.predefinedColors.length
        ]
        const newTag = new FavoriteTag(catalogCode, userId, {
          Tag: tagName,
          Color: color,
          CreatedByUserName: username,
          Articles: articleIds,
          LocalUpdatedDate: new Date(),
        })
        existingTagsByName[tagName] = newTag
        tagsToCreateOrUpdate[tagName] = newTag
      }
    }

    await Promise.all(
      Object.values(tagsToCreateOrUpdate).map(tag =>
        appConfig.DB!.createUpdateFavorite(catalogCode, userId, tag, userStore.currentCustomer ? userStore.currentCustomer.CustomerId : -1),
      ),
    )

    const favoriteTagsAtt = userStore.myAttributes!._FavoriteTags
    if (favoriteTagsAtt) {
      await updateFavoritesTagFilter(favoriteTagsAtt)
    }
    notificationsStore.addNotification({ message: t('favorites.messages.importSuccess'), type: 'Success' })
  }
  catch (error) {
    console.error('Import failed:', error)
    notificationsStore.addNotification({ message: t('favorites.messages.importFailed'), type: 'Alert' })
  }
}

onMounted(async () => {
  favoritesModel.value = new FavoritesModel(t)
  await fetchFavoriteTags()
  onAppEvent('catalogDataUpdated', fetchFavoriteTags)
  sBar.setItems([
    { id: 'ttlArts', label: t('status.totalArticles'), value: totalUniqueArticles.value.toLocaleString(), icon: '' },
    { id: 'listedArts', label: t('status.listedArticles'), value: '0', icon: '' },
    { id: 'selectedArticlesCount', label: t('status.selectedArticles'), value: '0', icon: '' },
  ])

  gotoRouteSelection()
  asideOpened.value = true
})

onUnmounted(() => {
  selectedTagId.value = 0
  offAppEvent('catalogDataUpdated', fetchFavoriteTags)
})
watch(() => [route.params, route.query.t], gotoRouteSelection)

watch(totalUniqueArticles, (newValue) => {
  sBar.setItemValue('ttlArts', newValue.toLocaleString())
})
</script>

<style lang="scss" scoped>
#catalog {
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
